import { React, useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import { Table, TableHeader, TableRow } from 'component/Table';
import { ButtonComponent } from 'component/Button';
import { toast } from 'react-toastify';
import { axiosWrapper, getPageValue } from 'utils/Helper';
import { ReactComponent as ViewIcon } from 'assets/images/icons/Eye-on.svg';
import { ReactComponent as EditIcon } from 'assets/images/icons/mode_edit_24px.svg';
import { ReactComponent as DeleteIcon } from 'assets/images/icons/delete_24px.svg';
import { ReactComponent as CancelSvg } from 'assets/images/icons/close_24px.svg';
import { useUser } from 'routes/UserContext';
import { getOrganizations, deleteOrganization } from 'api/OrganizationsApi';
import { OrganizationDialog } from './OrganizationDialog';
import 'assets/styles/pages/organizations.scss';

export const Organizations = ({ setLoader }) => {
  const [organizations, setOrganizations] = useState([]);
  const [page, setPage] = useState(1);
  const [next, setNext] = useState(false);
  const [listLoader, setListLoader] = useState(false);
  const [orgToDelete, setOrgToDelete] = useState(null);
  const { user } = useUser();

  const canManage = user?.permissions?.manage_organizations;

  const loadOrganizations = (pageAfterCreate = false) => {
    axiosWrapper(() => {
      const targetPage = pageAfterCreate || page;
      getOrganizations(targetPage)
        .then((res) => {
          const { results, next: nextUrl } = res.data;
          if (pageAfterCreate) {
            setOrganizations(results);
          } else {
            setOrganizations((prev) => [...prev, ...results]);
          }
          setNext(nextUrl);
          if (nextUrl) {
            setPage(Number(getPageValue(nextUrl)));
          }
        })
        .catch(() => {
          toast.error('Failed to load organizations');
          setOrganizations([]);
        })
        .finally(() => {
          setLoader(false);
          setListLoader(false);
        });
    });
  };

  useEffect(() => {
    setLoader(true);
    loadOrganizations();
  }, []);

  const handleRefresh = () => {
    setPage(1);
    loadOrganizations(1);
  };

  const handleDelete = (org) => {
    axiosWrapper(() => {
      deleteOrganization(org.id)
        .then(() => {
          toast.success('Organization deleted');
          handleRefresh();
          setOrganizations((prev) =>
            prev.filter((organization) => organization.id !== organization.id)
          );
        })
        .catch((err) => {
          const msg =
            err?.response?.data?.detail || 'Cannot delete: organization is used in reports';
          toast.error(msg);
        });
    });
  };

  return (
    <div className="organizations flex-column-group">
      <Table>
        <TableHeader>
          <th>Name</th>
          <th>Short name</th>
          <th>IMSMA</th>
          <th>Status</th>
          <th>Actions</th>
        </TableHeader>

        {!organizations.length && (
          <TableRow>
            <td colSpan={5}>Дані відсутні</td>
          </TableRow>
        )}

        {organizations.map((organization) => (
          <TableRow key={organization.id} className="org-row-compact">
            <td>{organization.full_name_uk || '—'}</td>
            <td>{organization.name}</td>
            <td>{organization.abbreviation}</td>
            <td>
              <span
                className={`org-status-badge ${organization.is_active ? 'org-status-active' : 'org-status-inactive'}`}
              >
                {organization.is_active ? 'Active' : 'Inactive'}
              </span>
            </td>
            <td className="icons org-actions">
              <OrganizationDialog
                mode="view"
                orgId={organization.id}
                triggerIcon={<ViewIcon className="view-icon" style={{ color: '#868686' }} />}
              />
              {canManage && (
                <>
                  <OrganizationDialog
                    mode="edit"
                    orgId={organization.id}
                    onSuccess={handleRefresh}
                    triggerIcon={<EditIcon className="edit-icon" style={{ color: '#868686' }} />}
                  />
                  <button
                    type="button"
                    className="icon-action-btn"
                    onClick={() => setOrgToDelete(organization)}
                    title="Delete"
                  >
                    <DeleteIcon className="delete-icon" style={{ color: '#868686' }} />
                  </button>
                </>
              )}
            </td>
          </TableRow>
        ))}

        {next && (
          <div className="loadMore-div">
            <button
              className="loadMore-button"
              onClick={() => {
                setListLoader(true);
                loadOrganizations();
              }}
            >
              {listLoader ? 'Loading...' : 'Load more...'}
            </button>
          </div>
        )}
      </Table>

      {canManage && (
        <div className="organizations-footer">
          <OrganizationDialog mode="create" onSuccess={handleRefresh} />
        </div>
      )}
      <Dialog
        open={!!orgToDelete}
        onClose={() => setOrgToDelete(null)}
        aria-labelledby="delete-organization-dialog"
        aria-describedby="delete-organization-description"
      >
        <div className="confirm-popup">
          <div className="header">
            <span className="title">Delete organization</span>
            <CancelSvg className="closeBtn" onClick={() => setOrgToDelete(null)} />
          </div>
          <div className="message" id="delete-organization-description">
            {`Do you really want to delete organization "${orgToDelete?.name}"?`}
          </div>
          <div className="buttons-group">
            <ButtonComponent
              className="danger"
              iconLeft={<DeleteIcon />}
              label="Yes"
              handleButton={() => {
                if (orgToDelete) {
                  handleDelete(orgToDelete);
                }
                setOrgToDelete(null);
              }}
            />
            <ButtonComponent
              iconLeft={<CancelSvg />}
              label="No"
              handleButton={() => setOrgToDelete(null)}
            />
          </div>
        </div>
      </Dialog>
    </div>
  );
};
